import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Typography, Button, Card, Divider, message } from 'antd';
import { CheckCircleOutlined, GlobalOutlined, SaveOutlined } from '@ant-design/icons';
import CommonForm from '../../../global/form';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import SOURCES from '../../../../utils/constants';
import { cn } from '../../../../utils/cn';
import Spinner from '../../../global/spinner';
import CommonInput from '../../../global/input';
import CommonTable from '../../../global/table';

const { Title, Text } = Typography;

const EnhancedNurturePro = ({ destinations, activeDestination, btnClassName }) => {
    const [form] = Form.useForm();

    const [locationData, setLocationData] = useState(null);
    const [isValidated, setIsValidated] = useState(false);
    const hasValidatedOnce = useRef(false);
    console.log(destinations)

    /* ---------------- SAVE CONFIG ---------------- */
    const { mutate: saveConfig, isPending: isSaving } = useGenericMutation({
        endpoint: 'integration',
        method: 'POST',
        onSuccess: () => {
            message.success(`${activeDestination.title} configuration saved successfully`);
        }
    });

    /* ---------------- VALIDATE LOCATION ---------------- */
    const { mutate: validateConfig, isPending: isLocationLoading } = useGenericMutation({
        endpoint: `integration/gohighlevel-location-details`,
        method: 'POST',
        onSuccess: (res, variables) => {
            setLocationData(res?.data || null); // make sure it's an object/array depending on API
            message.success(`${activeDestination.title} configuration validated successfully`);
        },
        onError: (error) => {
            setLocationData(null);
            setIsValidated(false);
            message.error(
                error?.response?.data?.data?.error ||
                `${activeDestination.title} configuration validation failed`
            );
        }
    });

    /* ---------------- RESET ON DESTINATION CHANGE ---------------- */
    useEffect(() => {
        setLocationData(null);
        hasValidatedOnce.current = false;
        form.resetFields();
    }, [activeDestination.company]);

    /* ---------------- PREFILL + AUTO VALIDATE (ONCE) ---------------- */
    useEffect(() => {
        if (!destinations || !activeDestination.company) return;

        const config = destinations.find(d => d.integrationCompany === activeDestination.company);
        if (!config) return;

        form.setFieldsValue({
            privateIntegrationToken: config.secretKey,
            locationId: config.publicKey,
            sendLeadsLimit: config.leadSentConfig?.sentLimit || 0
        });

        // Auto validate once
        if (config.secretKey && config.publicKey && !hasValidatedOnce.current) {
            hasValidatedOnce.current = true;
            validateConfig({
                privateIntegrationToken: config.secretKey,
                locationId: config.publicKey
            });
        }
    }, [destinations, activeDestination.company]);

    /* ---------------- SUBMIT ---------------- */
    const onFinish = (values) => {
        saveConfig({
            secretKey: values.privateIntegrationToken,
            publicKey: values.locationId,
            sendLeadsLimit: values.sendLeadsLimit,
            integrationCompany: activeDestination.company
        });
    };

    /* ---------------- TABLE COLUMNS ---------------- */
    const locationColumns = useMemo(() => [
        { title: 'Name', dataIndex: 'name', render: text => <Text strong>{text || 'N/A'}</Text> },
        {
            title: 'Address',
            dataIndex: 'address',
            render: (_, record) => (
                <Text>
                    {[record.address, record.city, record.state].filter(Boolean).join(', ') || 'N/A'}
                </Text>
            )
        },
        { title: 'Phone', dataIndex: 'phone' },
        { title: 'Timezone', dataIndex: 'timezone' }
    ], []);

    /* ---------------- FORM DATA ---------------- */
    const formData = useMemo(() => [
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: 'Private Integration Token',
            name: 'privateIntegrationToken',
            placeholder: 'Enter Private Integration Token',
            required: true,
            span: 24
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            label: 'Business Location Id',
            span: 24,
            component: (
                <div className="flex items-center gap-x-2">
                    <Form.Item
                        name="locationId"
                        noStyle
                        rules={[{ required: true, message: 'Location Id is required' }]}
                    >
                        <CommonInput
                            placeholder="Enter Location Id"
                            className="w-full"
                        />
                    </Form.Item>

                    <Button
                        type="primary"
                        className="h-10 !bg-primary text-white flex items-center gap-2"
                        disabled={isLocationLoading}
                        onClick={() => {
                            const values = form.getFieldsValue(['privateIntegrationToken', 'locationId']);
                            if (!values.privateIntegrationToken || !values.locationId) {
                                message.error('Please enter both Private Integration Token and Location Id');
                                return;
                            }
                            validateConfig(values);
                        }}
                    >
                        {isLocationLoading && <Spinner className="!border-white" />}
                        Validate
                    </Button>
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.CUSTOM,
            span: 24,
            className: locationData ? '' : '!hidden',
            component: locationData && (
                <div>
                    <Text className="block mb-2 text-sm font-semibold tracking-wider">Location details</Text>
                    <CommonTable
                        columns={locationColumns}
                        data={Array.isArray(locationData) ? locationData : [locationData]} // ensure table gets array
                        showActions={false}
                    />
                </div>
            )
        },
        {
            inputType: SOURCES.FORMINPUTTYPES.INPUT,
            label: 'Initial Lead Send Limit',
            name: 'sendLeadsLimit',
            type: 'number',
            placeholder: "Enter lead sent limit",
            span: 24
        }
    ], [locationData, activeDestination.title, isLocationLoading, isValidated]);

    const isDisabled = !locationData; // Save enabled only if locationData exists

    return (
        <div className="animate-in fade-in duration-500">
            <div className="flex items-center gap-4 mb-8">
                <div className="size-16 rounded-2xl bg-white shadow-sm border flex items-center justify-center p-3">
                    <img
                        src={activeDestination.icon}
                        alt={activeDestination.title}
                        className="w-full h-full object-contain"
                    />
                </div>
                <div>
                    <Title level={3}>{activeDestination.title} Configuration</Title>
                    <Text type="secondary">
                        Connect your {activeDestination.title} account and sync leads.
                    </Text>
                </div>
            </div>

            {/* NOTES */}
            <div className="bg-primary/10 p-3 rounded-xl mb-4">
                <div className="flex items-center gap-3">
                    <GlobalOutlined className="text-primary mt-0" />
                    <Text className="text-sm text-primary">
                        <Text strong className="text-primary">NOTE:</Text> You can get your {activeDestination.company === SOURCES.DESTINATION_GOHIGHLEVEL ? 'API key' : 'Token key'} from Setting → {activeDestination.company === SOURCES.DESTINATION_GOHIGHLEVEL ? 'Business Profile' : 'Private Integrations'} in your {activeDestination.title} dashboard.
                    </Text>
                </div>
                <div className="space-y-2 mt-2">
                    <h1 className="text-base font-semibold">Scopes</h1>
                    <div className="flex flex-col items-start gap-2">
                        {['contacts.write', 'contacts.readonly', 'locations.readonly', "locations/customFields.readonly", "locations/customFields.write"].map(scope => (
                            <div key={scope} className="flex items-center gap-2">
                                <CheckCircleOutlined className="text-primary" />
                                <Text>{scope}</Text>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-primary/10 p-3 rounded-xl mb-6">
                <div className="flex items-center gap-3">
                    <GlobalOutlined className="text-primary mt-0" />
                    <Text className="text-sm text-primary">
                        <Text strong className="text-primary">NOTE:</Text> You can get your Location Id from Setting → Business Profile in your {activeDestination.title} dashboard.
                    </Text>
                </div>
            </div>

            <Card className="rounded-2xl border-none shadow-sm">
                <CommonForm
                    form={form}
                    data={formData}
                    onFinish={onFinish}
                    layoutType="vertical"
                />

                <Divider />

                <Button
                    size="large"
                    icon={!isSaving && <SaveOutlined />}
                    onClick={() => form.submit()}
                    className={cn(isDisabled && '!opacity-50 !cursor-not-allowed', btnClassName)}
                    disabled={isDisabled}
                >
                    {isSaving && <Spinner className="!border-white" />}
                    Save Configuration
                </Button>
            </Card>
        </div>
    );
};

export default EnhancedNurturePro;
